/**
 * High score system for single player games
 * Reference: quadra/source/highscores.cc:128-179 (Highscores::update)
 */

export interface HighScoreEntry {
  name: string;
  score: number;
  level: number;
  lines: number;
}

const MAX_SCORE = 10;
const HIGH_SCORES_KEY = "quadra_high_scores";

/**
 * Load high scores from localStorage
 * Reference: quadra/source/highscores.cc:45-108 (Highscores::load)
 */
export function loadHighScores(): HighScoreEntry[] {
  try {
    const stored = localStorage.getItem(HIGH_SCORES_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error("Failed to load high scores:", e);
  }
  return [];
}

/**
 * Save high scores to localStorage
 */
function saveHighScores(scores: HighScoreEntry[]): void {
  try {
    localStorage.setItem(HIGH_SCORES_KEY, JSON.stringify(scores));
  } catch (e) {
    console.error("Failed to save high scores:", e);
  }
}

/**
 * Update high scores with a new entry
 * Reference: quadra/source/highscores.cc:128-179 (Highscores::update)
 * @param canvasScore The player's score
 * @param canvasLevel The player's level
 * @param canvasLines The player's lines cleared
 * @param playerName The player's name
 * @returns The rank of the new high score (-1 if not a high score)
 */
export function updateHighScores(
  canvasScore: number,
  canvasLevel: number,
  canvasLines: number,
  playerName: string
): number {
  const scores = loadHighScores();
  let ret = -1;

  // Find insertion point
  for (let i = 0; i < scores.length; i++) {
    if (canvasScore >= scores[i].score) {
      ret = i;
      break;
    }
  }

  // If we have less than MAX_SCORE entries and didn't find a position, add at end
  if (scores.length < MAX_SCORE && ret === -1) {
    ret = scores.length;
    scores.length++;
  }

  // If we found a position, insert the new score
  if (ret !== -1) {
    // Shift scores down
    for (let j = scores.length - 1; j > ret; j--) {
      if (scores[j - 1]) {
        scores[j] = { ...scores[j - 1] };
      }
    }

    // Insert new score
    scores[ret] = {
      name: playerName,
      score: canvasScore,
      level: canvasLevel,
      lines: canvasLines,
    };

    // Trim to MAX_SCORE
    if (scores.length > MAX_SCORE) {
      scores.length = MAX_SCORE;
    }

    saveHighScores(scores);
  }

  return ret;
}

